import { useCallback, useEffect, useRef, useState } from 'react';
import commercialDashboardService from '../services/commercialDashboardService';
import { CommercialDashboard, CommercialDashboardFilters } from '../utils/commercialDashboard';

export const COMMERCIAL_DASHBOARD_POLL_MS = 20_000;

export const useCommercialDashboard = (filters: CommercialDashboardFilters) => {
  const [data, setData] = useState<CommercialDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const latestRequest = useRef(0);
  const { period, client_id: clientId, project_type: projectType, owner_id: ownerId } = filters;

  const load = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setRefreshing(true);
    try {
      const next = await commercialDashboardService.getDashboard({
        period,
        client_id: clientId,
        project_type: projectType,
        owner_id: ownerId,
      });
      if (requestId !== latestRequest.current) return;
      setData(next);
      setError(null);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error(err);
      setError('Could not load the commercial dashboard.');
    } finally {
      if (requestId === latestRequest.current) setRefreshing(false);
    }
  }, [period, clientId, projectType, ownerId]);

  useEffect(() => {
    void load();

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, COMMERCIAL_DASHBOARD_POLL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void load();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      latestRequest.current += 1;
    };
  }, [load]);

  return { data, error, refreshing, refresh: load };
};
